import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Heart, Loader2, Lock, Sparkles, Crown, Star, Send, MapPin, Clock } from 'lucide-react';
import DemoProfileBadge from '@/components/DemoProfileBadge';
import VerifiedBadge from '@/components/VerifiedBadge';

function getPhotoUrl(p) {
  if (!p) return null;
  if (p.startsWith('http')) return p;
  return `${window.location.origin}/api/files/${p}`;
}

const PAGE_SIZE = 30;

// Feb 2026 · Web parity with mobile handoff §8 + §9 · 5-filter chip sets.
// SENT tab drives off swipe fields (like_kind, is_super, is_intro, compat).
// VIEWS tab drives off viewed_at + opened-locally + Liked-You intersection.
//
// The "Your Type" threshold is now backend-configured — see /api/config.
// This constant is the fallback if the config fetch fails at mount time.
const YOUR_TYPE_MIN_COMPAT_FALLBACK = 70;

const SENT_FILTERS = [
  { key: 'all',    label: 'All',       icon: Heart,     tint: 'text-zinc-500' },
  { key: 'intros', label: 'Intros',    icon: Send,      tint: 'text-[#2563EB]' },
  { key: 'roses',  label: 'Roses',     icon: Star,      tint: 'text-[#7C3AED]' },
  { key: 'likes',  label: 'Likes',     icon: Heart,     tint: 'text-[#DC2626]' },
  { key: 'type',   label: 'Your Type', icon: MapPin,    tint: 'text-[#0F0F10]' },
];

const VIEWS_FILTERS = [
  { key: 'all',    label: 'All',       icon: Heart,     tint: 'text-zinc-500' },
  { key: 'recent', label: 'Recent',    icon: Clock,     tint: 'text-[#0F0F10]' },
  { key: 'new',    label: 'New',       icon: Sparkles,  tint: 'text-[#B91C1C]' },
  { key: 'liked',  label: 'Liked You', icon: Heart,     tint: 'text-[#EF4444]' },
  { key: 'type',   label: 'Your Type', icon: MapPin,    tint: 'text-[#0F0F10]' },
];

// LocalStorage key MUST match mobile so the "new" bucket stays consistent
// when a user moves between platforms.
const VIEWED_OPENED_LS_KEY = 'mtk.viewed_you.opened_viewer_ids.v1';

function loadOpenedViewers() {
  try {
    const raw = localStorage.getItem(VIEWED_OPENED_LS_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}
function saveOpenedViewers(set) {
  try { localStorage.setItem(VIEWED_OPENED_LS_KEY, JSON.stringify([...set])); }
  catch { /* localStorage may be blocked in private mode — non-critical */ }
}

// Pure filter functions — keep them out of the component body for readability.
// `yourTypeMin` is passed in so it's driven by the /api/config fetch.
function filterSent(profiles, key, yourTypeMin) {
  switch (key) {
    case 'intros': return profiles.filter(p => p.is_intro === true);
    case 'roses':  return profiles.filter(p => p.is_super === true);
    case 'likes':  return profiles.filter(p => p.like_kind === 'like' || (!p.is_super && !p.is_intro));
    case 'type':   return profiles.filter(p => (p.compatibility_score || 0) >= yourTypeMin);
    case 'all':
    default:       return profiles;
  }
}

function filterViews(viewers, key, likedYouIds, openedIds, yourTypeMin) {
  switch (key) {
    case 'recent':
      return viewers;
    case 'new':
      return viewers.filter(v => !openedIds.has(v.user_id));
    case 'liked':
      return viewers.filter(v => likedYouIds.has(v.user_id));
    case 'type':
      return viewers.filter(v => (v.compatibility_score || 0) >= yourTypeMin);
    case 'all':
    default:
      return viewers;
  }
}

export default function LikesReceived() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('received'); // 'received' | 'sent' | 'views'
  const [loading, setLoading] = useState(true);
  const [received, setReceived] = useState({ profiles: [], total: 0, tier: 'free', locked: true, has_more: false });
  const [sent, setSent] = useState({ profiles: [], total: 0, has_more: false });
  const [views, setViews] = useState({ viewers: [], total: 0, tier: 'free', locked: true });
  const [swiping, setSwiping] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const recvOffsetRef = useRef(0);
  const sentOffsetRef = useRef(0);
  const sentinelRef = useRef(null);

  // Feb 2026 · 5-filter chip sets (mobile parity — see mobile handoff §8/§9).
  const [sentFilter, setSentFilter] = useState('all');
  const [viewsFilter, setViewsFilter] = useState('all');
  const [openedViewerIds, setOpenedViewerIds] = useState(() => loadOpenedViewers());
  // Feb 18 2026 — backend-driven "Your Type" threshold (see /api/config).
  // Falls back to the local constant if the fetch fails.
  const [yourTypeMin, setYourTypeMin] = useState(YOUR_TYPE_MIN_COMPAT_FALLBACK);

  useEffect(() => {
    let cancelled = false;
    api.get('/config').then(r => {
      if (cancelled) return;
      const v = Number(r.data?.your_type_min_compat);
      if (Number.isFinite(v) && v >= 0 && v <= 100) setYourTypeMin(v);
      // Feb 18 2026 — one-shot A/B variant log. `analytics` is the same
      // helper the rest of the app uses; falling back to a no-op keeps
      // this safe if we're on a plain build without PostHog wired.
      const variant = r.data?.experiments?.your_type_min_compat_v1;
      if (variant) {
        try {
          const seenKey = `mtk.ab.your_type_min_compat_v1.logged`;
          if (!sessionStorage.getItem(seenKey)) {
            sessionStorage.setItem(seenKey, '1');
            window.posthog?.capture?.('ab_variant_assigned', {
              experiment: 'your_type_min_compat_v1', variant, value: v,
            });
          }
        } catch { /* sessionStorage / posthog may be blocked — non-critical */ }
      }
    }).catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
  }, []);

  const likedYouIds = useMemo(
    () => new Set((received.profiles || []).map(p => p.user_id)),
    [received.profiles]
  );

  const markViewerOpened = useCallback((viewerId) => {
    setOpenedViewerIds(prev => {
      if (prev.has(viewerId)) return prev;
      const next = new Set(prev); next.add(viewerId);
      saveOpenedViewers(next);
      return next;
    });
    // Feb 18 2026 — cross-platform "New" bucket sync. Best-effort; failures
    // are silent (offline / rate-limit / whatever). Client cache stays
    // truthful either way — server just catches up next time.
    api.post(`/profile-views/${viewerId}/mark_opened`).catch(() => {});
  }, []);

  const loadReceived = useCallback(async (reset = false) => {
    const offset = reset ? 0 : recvOffsetRef.current;
    try {
      const r = await api.get(`/likes-received?offset=${offset}&limit=${PAGE_SIZE}`);
      const pageItems = r.data.profiles || [];
      setReceived(prev => reset ? r.data : { ...r.data, profiles: [...prev.profiles, ...pageItems] });
      recvOffsetRef.current = offset + pageItems.length;
    } catch (e) { if (reset) toast.error('Failed to load likes'); }
  }, []);

  const loadSent = useCallback(async (reset = false) => {
    const offset = reset ? 0 : sentOffsetRef.current;
    try {
      const r = await api.get(`/likes-sent?offset=${offset}&limit=${PAGE_SIZE}`);
      const pageItems = r.data.profiles || [];
      setSent(prev => reset ? r.data : { ...r.data, profiles: [...prev.profiles, ...pageItems] });
      sentOffsetRef.current = offset + pageItems.length;
    } catch (e) { if (reset) toast.error('Failed to load sent likes'); }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([
        loadReceived(true),
        loadSent(true),
        api.get('/profile-views').then(r => {
          setViews(r.data);
          // Feb 18 2026 — union server-persisted `opened: true` with the
          // local set so viewers marked opened on another platform (mobile)
          // aren't shown as NEW here on first load.
          const serverOpened = (r.data?.viewers || [])
            .filter(v => v.opened)
            .map(v => v.user_id);
          if (serverOpened.length > 0) {
            setOpenedViewerIds(prev => {
              const next = new Set(prev);
              serverOpened.forEach(id => next.add(id));
              saveOpenedViewers(next);
              return next;
            });
          }
        }).catch(() => {}),
      ]);
      setLoading(false);
    })();
  }, [loadReceived, loadSent]);

  // Infinite scroll for the active tab
  useEffect(() => {
    if (loading) return;
    const active = tab === 'received' ? received : sent;
    if ((tab === 'received' && received.locked) || !active.has_more) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(async (entries) => {
      if (entries[0].isIntersecting && !loadingMore && active.has_more) {
        setLoadingMore(true);
        await (tab === 'received' ? loadReceived(false) : loadSent(false));
        setLoadingMore(false);
      }
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [tab, received.has_more, received.locked, sent.has_more, loading, loadingMore, loadReceived, loadSent, received, sent]);

  const swipeBack = async (userId, dir) => {
    setSwiping(userId);
    try {
      const r = await api.post('/swipe', { swiped_id: userId, direction: dir });
      if (r.data.match) {
        toast.success('It\'s a match!');
        setTimeout(() => navigate(`/chat/${r.data.match_data.match_id}`), 800);
      } else if (dir === 'left') {
        toast.success('Passed');
      }
      setReceived(d => ({ ...d, profiles: d.profiles.filter(p => p.user_id !== userId), total: Math.max(0, d.total - 1) }));
      // If they liked back, remove from sent tab too (now a match)
      if (r.data.match) setSent(d => ({ ...d, profiles: d.profiles.filter(p => p.user_id !== userId), total: Math.max(0, d.total - 1) }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSwiping(null); }
  };

  const retractLike = async (userId) => {
    setSwiping(userId);
    try {
      await api.delete(`/likes-sent/${userId}`);
      setSent(d => ({ ...d, profiles: d.profiles.filter(p => p.user_id !== userId), total: Math.max(0, d.total - 1) }));
      toast.success('Like retracted');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSwiping(null); }
  };

  if (loading) return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#EF4444]" /></div>;

  const data = tab === 'received' ? received : sent;

  return (
    <div className="min-h-screen bg-white pb-[96px]" data-testid="likes-received-page">
      <div className="px-5 pt-6 pb-4 flex items-center gap-3 border-b border-black/[0.04]">
        <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-full hover:bg-black/[0.03] flex items-center justify-center" data-testid="back-btn"><ArrowLeft size={18}/></button>
        <div>
          <h1 className="font-brand text-xl font-[600] tracking-tight">Likes</h1>
          <p className="text-xs text-zinc-500">
            {tab === 'received'
              ? <>{received.total} {received.total === 1 ? 'person likes' : 'people like'} you</>
              : <>You've liked {sent.total} {sent.total === 1 ? 'person' : 'people'}</>}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-5 pt-4 pb-2 flex gap-1 sticky top-0 bg-white z-10 border-b border-black/[0.04]" data-testid="likes-tabs">
        {[
          { id: 'received', label: 'Liked You', count: received.locked ? received.total : received.total, testId: 'tab-received' },
          { id: 'sent', label: 'You Liked', count: sent.total, testId: 'tab-sent' },
          { id: 'views', label: 'Viewed You', count: views.total || 0, testId: 'tab-views' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={t.testId}
            className={`flex-1 py-2.5 rounded-full text-[13px] font-semibold transition-all ${
              tab === t.id
                ? 'brand-gradient text-white shadow-[0_4px_12px_-2px_rgba(185,28,28,0.3)]'
                : 'bg-[var(--surface-2)] text-[#4B4B52] hover:bg-black/[0.04]'
            }`}>
            {t.label} {t.count > 0 && <span className={`ml-1.5 text-[11px] ${tab === t.id ? 'opacity-80' : 'opacity-60'}`}>{t.count}</span>}
          </button>
        ))}
      </div>

      {tab === 'views' ? (
        views.locked ? (
          <LockedView total={views.total || 0} onUpgrade={() => navigate('/subscription')}
            teaser={`${views.total || 0} ${views.total === 1 ? 'person' : 'people'} viewed your profile`} label="views" />
        ) : (views.viewers || []).length === 0 ? (
          <div className="px-6 py-20 text-center">
            <div className="w-16 h-16 rounded-full bg-[#F8F9FA] flex items-center justify-center mx-auto mb-4">
              <Heart className="w-7 h-7 text-zinc-400" />
            </div>
            <h2 className="text-lg font-bold mb-1">No profile views yet</h2>
            <p className="text-sm text-zinc-500">When people check you out, they'll show up here.</p>
          </div>
        ) : (
          <>
            <FilterChipRow filters={VIEWS_FILTERS} activeKey={viewsFilter} onChange={setViewsFilter} testIdPrefix="views-filter" />
            {(() => {
              const filtered = filterViews(views.viewers, viewsFilter, likedYouIds, openedViewerIds, yourTypeMin);
              if (filtered.length === 0) {
                const label = VIEWS_FILTERS.find(f => f.key === viewsFilter)?.label || 'matching';
                return (
                  <div className="px-6 py-16 text-center" data-testid="views-filter-empty">
                    <p className="text-sm text-zinc-500">No {label.toLowerCase()} yet.</p>
                    <button onClick={() => setViewsFilter('all')} className="mt-3 text-xs font-semibold text-[#B91C1C] hover:underline">Show all</button>
                  </div>
                );
              }
              return (
                <div className="p-4 grid grid-cols-2 gap-3" data-testid="views-grid">
                  {filtered.map(v => (
                    <LikeCard key={v.user_id} profile={v} mode="received"
                      isNew={!openedViewerIds.has(v.user_id)}
                      isLikedYou={likedYouIds.has(v.user_id)}
                      onLike={() => { markViewerOpened(v.user_id); swipeBack(v.user_id, 'right'); }}
                      onPass={() => { markViewerOpened(v.user_id); swipeBack(v.user_id, 'left'); }}
                      disabled={swiping === v.user_id} />
                  ))}
                </div>
              );
            })()}
          </>
        )
      ) : tab === 'received' && received.locked ? (
        <LockedView total={received.total} onUpgrade={() => navigate('/subscription')} />
      ) : data.profiles.length === 0 ? (
        <div className="px-6 py-20 text-center">
          <div className="w-16 h-16 rounded-full bg-[#F8F9FA] flex items-center justify-center mx-auto mb-4">
            <Heart className="w-7 h-7 text-zinc-400" />
          </div>
          <h2 className="text-lg font-bold mb-1">
            {tab === 'received' ? 'No likes yet' : 'You haven\'t liked anyone yet'}
          </h2>
          <p className="text-sm text-zinc-500">
            {tab === 'received'
              ? 'Keep being intentional. The right people will come.'
              : 'Start discovering matches and like the people who catch your eye.'}
          </p>
          {tab === 'sent' && (
            <button onClick={() => navigate('/discover')} data-testid="go-discover"
              className="mt-5 brand-gradient text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:scale-[1.02] transition-all">
              Go to Discover
            </button>
          )}
        </div>
      ) : (
        <>
          {tab === 'sent' && (
            <FilterChipRow filters={SENT_FILTERS} activeKey={sentFilter} onChange={setSentFilter} testIdPrefix="sent-filter" />
          )}
          {(() => {
            const listing = tab === 'sent' ? filterSent(data.profiles, sentFilter, yourTypeMin) : data.profiles;
            if (tab === 'sent' && listing.length === 0) {
              const label = SENT_FILTERS.find(f => f.key === sentFilter)?.label || 'matching';
              return (
                <div className="px-6 py-16 text-center" data-testid="sent-filter-empty">
                  <p className="text-sm text-zinc-500">No {label.toLowerCase()} yet.</p>
                  <button onClick={() => setSentFilter('all')} className="mt-3 text-xs font-semibold text-[#B91C1C] hover:underline">Show all</button>
                </div>
              );
            }
            return (
              <div className="p-4 grid grid-cols-2 gap-3" data-testid="likes-grid">
                {listing.map(p => (
                  tab === 'received' ? (
                    <LikeCard key={p.user_id} profile={p} mode="received"
                      onLike={() => swipeBack(p.user_id, 'right')}
                      onPass={() => swipeBack(p.user_id, 'left')}
                      swiping={swiping === p.user_id} />
                  ) : (
                    <LikeCard key={p.user_id} profile={p} mode="sent"
                      onRetract={() => retractLike(p.user_id)}
                      swiping={swiping === p.user_id} />
                  )
                ))}
                {data.has_more && (
                  <div ref={sentinelRef} className="col-span-2 flex items-center justify-center py-6" data-testid="likes-load-more-sentinel">
                    {loadingMore && <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />}
                  </div>
                )}
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}

function FilterChipRow({ filters, activeKey, onChange, testIdPrefix = 'filter-chip' }) {
  return (
    <div className="px-5 py-3 flex gap-2 overflow-x-auto no-scrollbar border-b border-black/[0.04]" data-testid={`${testIdPrefix}-row`}>
      {filters.map(f => {
        const Icon = f.icon;
        const active = f.key === activeKey;
        return (
          <button
            key={f.key}
            type="button"
            onClick={() => onChange(f.key)}
            data-testid={`${testIdPrefix}-${f.key}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold whitespace-nowrap border transition-all ${
              active
                ? 'brand-gradient text-white border-transparent shadow-[0_4px_10px_-2px_rgba(185,28,28,0.28)]'
                : 'bg-white text-[#4B4B52] border-black/[0.06] hover:bg-black/[0.03]'
            }`}
          >
            <Icon size={13} className={active ? 'text-white' : f.tint} />
            {f.label}
          </button>
        );
      })}
    </div>
  );
}

function LockedView({ total, onUpgrade, teaser, label = 'likes' }) {
  // Generate blurred silhouettes for visual impact
  const count = Math.min(total, 6);
  return (
    <div className="px-5" data-testid="likes-locked">
      <div className="grid grid-cols-2 gap-3 p-4 relative">
        {Array.from({length: count || 4}).map((_, i) => (
          <div key={i} className="aspect-[3/4] rounded-2xl overflow-hidden relative bg-gradient-to-br from-zinc-100 to-zinc-200">
            <div className="absolute inset-0 backdrop-blur-2xl" style={{background: `linear-gradient(135deg, ${['#FEE2E2','#FEF3C7','#DBEAFE','#F3E8FF','#D1FAE5','#FEE2E2'][i%6]} 0%, ${['#FECACA','#FDE68A','#BFDBFE','#E9D5FF','#A7F3D0','#FECACA'][i%6]} 100%)`}} />
            <div className="absolute inset-0 flex items-center justify-center">
              <Heart className="w-10 h-10 text-white/60" fill="currentColor" />
            </div>
          </div>
        ))}
        <div className="absolute inset-0 bg-white/40 backdrop-blur-sm pointer-events-none rounded-2xl" />
      </div>

      <div className="text-center px-6 py-8 -mt-8 relative z-10">
        <div className="w-14 h-14 rounded-2xl brand-gradient brand-glow flex items-center justify-center mx-auto mb-5">
          <Lock className="w-6 h-6 text-white" />
        </div>
        <h2 className="text-2xl font-extrabold tracking-tight mb-2">{label === 'views' ? 'See who viewed you' : 'See who likes you'}</h2>
        <p className="text-sm text-zinc-500 max-w-xs mx-auto mb-6 leading-relaxed">
          {teaser ? <>{teaser}. Upgrade to see their profiles.</> :
            (total > 0
              ? <>You have <span className="font-bold text-[#EF4444]">{total} {total === 1 ? 'person' : 'people'}</span> interested in you right now. Upgrade to see their profiles and match instantly.</>
              : <>Once people {label === 'views' ? 'view' : 'like'} you, see them here. Upgrade to match instantly without waiting.</>)
          }
        </p>
        <button onClick={onUpgrade} data-testid="upgrade-btn"
          className="w-full max-w-xs mx-auto brand-gradient text-white py-3.5 rounded-full text-sm font-semibold brand-glow hover:scale-[1.02] transition-all active:scale-[0.98] inline-flex items-center justify-center gap-2">
          <Crown size={16}/> Upgrade to Premium
        </button>
        <p className="text-[11px] text-zinc-400 mt-3">From $13.99/mo billed yearly</p>
      </div>
    </div>
  );
}

function LikeCard({ profile, mode = 'received', onLike, onPass, onRetract, swiping, isNew = false, isLikedYou = false }) {
  const photos = profile.photos?.length ? profile.photos : [profile.photo_url];
  return (
    <div className="relative rounded-2xl overflow-hidden bg-[#F8F9FA] border border-black/[0.04] shadow-sm hover:shadow-lg transition-all" data-testid={`like-card-${profile.user_id}`}>
      <div className="aspect-[3/4] relative">
        <img src={getPhotoUrl(photos[0])} alt="" className="w-full h-full object-cover" onError={e => { e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${profile.user_id}`; }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

        {/* Feb 2026 — Signal pill stack (mobile parity §8). Render regardless of active filter. */}
        {(isNew || isLikedYou) && (
          <div className="absolute top-2 left-2 flex flex-col gap-1 items-start">
            {isNew && (
              <span className="px-1.5 py-0.5 rounded-full bg-white/90 backdrop-blur text-[9px] font-bold tracking-wider text-[#B91C1C] uppercase" data-testid={`pill-new-${profile.user_id}`}>New</span>
            )}
            {isLikedYou && (
              <span className="px-1.5 py-0.5 rounded-full bg-[#EF4444]/95 backdrop-blur text-[9px] font-bold tracking-wider text-white uppercase" data-testid={`pill-liked-${profile.user_id}`}>Liked you</span>
            )}
          </div>
        )}

        {profile.is_super && (
          <div className={`absolute ${(isNew || isLikedYou) ? 'top-2 right-14' : 'top-2 left-2'} px-2 py-1 rounded-full bg-[#3B82F6]/90 backdrop-blur flex items-center gap-1`}>
            <Star size={10} className="text-white" fill="currentColor" />
            <span className="text-[10px] font-bold text-white uppercase tracking-wider">Super</span>
          </div>
        )}

        <div className="absolute top-2 right-2 w-10 h-10 rounded-full bg-white/15 backdrop-blur flex items-center justify-center border border-white/30">
          <span className="text-[11px] font-bold text-white">{profile.compatibility_score || 50}%</span>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-3">
          {profile.is_seed && <div className="mb-1"><DemoProfileBadge isSeed={true} size="xs" /></div>}
          <h3 className="text-sm font-bold text-white inline-flex items-center gap-1">
            <span>{profile.display_name}, {profile.age}</span>
            <VerifiedBadge badges={profile.badges} size={13} />
          </h3>
          {profile.location?.city && <p className="text-[11px] text-white/70 truncate">{profile.location.city}</p>}
        </div>
      </div>

      <div className="flex border-t border-black/[0.04]">
        {mode === 'received' ? (
          <>
            <button onClick={onPass} disabled={swiping} data-testid={`pass-${profile.user_id}`}
              className="flex-1 py-2.5 text-xs font-semibold text-zinc-500 hover:bg-black/[0.02] transition-colors">
              Pass
            </button>
            <div className="w-px bg-black/[0.04]" />
            <button onClick={onLike} disabled={swiping} data-testid={`like-back-${profile.user_id}`}
              className="flex-1 py-2.5 text-xs font-bold text-[#EF4444] hover:bg-[#EF4444]/5 transition-colors inline-flex items-center justify-center gap-1">
              {swiping ? <Loader2 size={12} className="animate-spin"/> : <><Heart size={12} fill="currentColor"/> Match</>}
            </button>
          </>
        ) : (
          <>
            <div className="flex-1 py-2.5 text-xs font-semibold text-zinc-500 inline-flex items-center justify-center gap-1">
              <Heart size={12} className="text-[#EF4444]" fill="currentColor"/> You liked
            </div>
            <div className="w-px bg-black/[0.04]" />
            <button onClick={onRetract} disabled={swiping} data-testid={`retract-${profile.user_id}`}
              className="flex-1 py-2.5 text-xs font-semibold text-zinc-500 hover:text-[#4B4B52] hover:bg-black/[0.02] transition-colors inline-flex items-center justify-center gap-1">
              {swiping ? <Loader2 size={12} className="animate-spin"/> : 'Unlike'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
